<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import type { VaultFileRevisionInDb } from '@/api';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import {
  gaiaDiff,
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '@/components/design/themes/tokens';

const props = defineProps({
  fileUid: {
    type: String,
    required: true,
  },
  revision: {
    type: Object as PropType<VaultFileRevisionInDb | null>,
    default: null,
  },
  /**
   * Which side of the comparison this is. `a` is the older revision and carries
   * the removed tones; `b` is the newer one and carries the added tones — the
   * same pairing the diff list and the PDF marks use, so a colour means one
   * thing everywhere on this screen.
   */
  side: {
    type: String as PropType<'a' | 'b'>,
    required: true,
  },
});

const emit = defineEmits<{ loaded: []; 'page-change': [page: number] }>();

const previewRef = ref<InstanceType<typeof GaiaVaultFilePdfPreview>>();

const isOlder = computed(() => props.side === 'a');

function formatBytes(bytes: number | null | undefined): string {
  // A draft's revision stores no file (it is rendered on read), so it has no size.
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

/**
 * The preview's imperative API is documented as a fixed contract for its
 * callers, so this wrapper forwards it verbatim rather than re-deriving it —
 * the compare view drives page jumps, re-rasterisation after a split drag, and
 * the per-page highlight sets through exactly these three.
 */
defineExpose({
  goToPage: (page: number) => previewRef.value?.goToPage(page),
  reloadPDF: () => previewRef.value?.reloadPDF(),
  setHighlightTextDirect: (texts: string[]) => previewRef.value?.setHighlightTextDirect(texts),
});

const headBorder = gaiaNeutral.borderMuted;
const monoFont = gaiaFonts.mono;
const markSize = gaiaFontSize.nano;
const markWeight = String(gaiaFontWeight.bold);
const addedStrong = gaiaDiff.addedStrong;
const addedBadge = gaiaDiff.addedBadgeSurface;
const removedStrong = gaiaDiff.removedStrong;
const removedBadge = gaiaDiff.removedBadgeSurface;
</script>

<template>
  <div class="pane">
    <!-- Which revision this side is showing. The version pill is the only place
         the two documents are told apart once both are scrolled into the body. -->
    <div class="head">
      <span class="version" :class="isOlder ? 'older' : 'newer'">
        v{{ props.revision?.version_number }}
      </span>
      <gaia-text truncate size="control" weight="semibold" tone="ink">
        {{ props.revision?.file_name }}
      </gaia-text>
      <gaia-text v-if="props.revision" size="meta" tone="faint" :style="{ marginLeft: 'auto' }">
        · {{ formatBytes(props.revision.size_b) }}
      </gaia-text>
    </div>

    <div class="body">
      <gaia-vault-file-pdf-preview
        v-if="props.revision"
        ref="previewRef"
        :vault-file-uid="props.fileUid"
        :revision-uid="props.revision.uid"
        :revision-preview-status="props.revision.preview_status ?? null"
        :highlight-color="isOlder ? 'red' : 'green'"
        @loaded="emit('loaded')"
        @page-change="page => emit('page-change', page)"
      />
    </div>
  </div>
</template>

<style scoped>
.pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  height: 40px;
  padding: 0 12px;
  overflow: hidden;
  border-bottom: 1px solid v-bind(headBorder);
}

/* A bordered mono pill rather than GaiaTag: the version is a number to compare
   against the other pane's, so it takes the tabular face and the diff tones. */
.version {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 20px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: 10px;
  font-family: v-bind(monoFont);
  font-size: v-bind(markSize);
  font-weight: v-bind(markWeight);
}

.version.older {
  border-color: v-bind(removedStrong);
  background: v-bind(removedBadge);
  color: v-bind(removedStrong);
}

.version.newer {
  border-color: v-bind(addedStrong);
  background: v-bind(addedBadge);
  color: v-bind(addedStrong);
}

.body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
